import React, { useEffect, useMemo, useState } from 'react'
import { useHistory } from 'react-router'

import classnames from 'classnames'
import { ChevronRightIcon, CloseIcon } from '@toasttab/buffet-pui-icons'
import { t } from 'src/translations'
import { useRestaurantRoutes } from 'shared/components/common/restaurant_routes/RestaurantRoutesContext'

import { LeftMenuPanelProps } from './LeftMenuPanel'
import { filterMenuGroupsByItemCount } from './shared'
import { MenuGroup } from './types'
import { navigateToCategory } from './urlFilterUtils'

export const DesktopLeftNav = ({
  isOpen,
  menuGroups,
  selectedGroup,
  initialSelectedChild,
  currentCategorySlug,
  onClose,
  onGroupSelect,
  setFilters
}: LeftMenuPanelProps) => {
  const history = useHistory()
  const { ecommPath } = useRestaurantRoutes()
  const [internalSelectedGroup, setInternalSelectedGroup] =
    useState<MenuGroup | null>(selectedGroup)
  const [selectedChild, setSelectedChild] = useState<MenuGroup | null>(
    initialSelectedChild || null
  )

  useEffect(() => {
    setInternalSelectedGroup(selectedGroup)
    // Only reset child selection if no initialSelectedChild is provided
    setSelectedChild(initialSelectedChild || null)
  }, [selectedGroup, initialSelectedChild])

  const applyFilter = (filterString: string) => {
    setFilters(new Set([filterString]))
    onClose()
  }

  // Filter out groups with 0 items
  const filteredMenuGroups = useMemo(
    () => filterMenuGroupsByItemCount(menuGroups),
    [menuGroups]
  )

  if (!isOpen) return null

  const handleGroupClick = (group: MenuGroup) => {
    // If this group has no children
    if (
      !group.children ||
      filterMenuGroupsByItemCount(group.children).length === 0
    ) {
      if (group.slug) {
        navigateToCategory(group, ecommPath, history, onClose)
      } else {
        applyFilter(group.guid)
      }
    } else {
      // If it has children, set it as selected to show the children column
      setInternalSelectedGroup(group)
      setSelectedChild(null) // Reset child selection when switching groups
      onGroupSelect?.(group)
    }
  }

  const handleChildClick = (child: MenuGroup) => {
    // If this child has no children
    if (
      !child.children ||
      filterMenuGroupsByItemCount(child.children).length === 0
    ) {
      if (child.slug) {
        navigateToCategory(child, ecommPath, history, onClose)
      } else {
        const parentGroup = internalSelectedGroup
        if (parentGroup) {
          applyFilter(`${parentGroup.guid}:${child.guid}`)
        }
      }
    } else {
      // If it has children, just set it as selected to show the third column
      setSelectedChild(child)
    }
  }

  const getPanelClass = () => {
    if (selectedChild) return 'fullExpanded'
    if (currentGroup) return 'expanded'
    return ''
  }

  const currentGroup = internalSelectedGroup || selectedGroup
  // Filter children of the current group as well
  const filteredCurrentGroup = currentGroup
    ? {
        ...currentGroup,
        children: currentGroup.children
          ? filterMenuGroupsByItemCount(currentGroup.children)
          : currentGroup.children
      }
    : null

  const filteredSelectedChild = selectedChild
    ? {
        ...selectedChild,
        children: selectedChild.children
          ? filterMenuGroupsByItemCount(selectedChild.children)
          : selectedChild.children
      }
    : null

  return (
    <div
      data-testid='panelOverlay'
      className='panelOverlay'
      onClick={onClose}
      style={{ zIndex: 1000 }}
    >
      <div
        className={`leftPanel ${getPanelClass()}`}
        onClick={(e) => e.stopPropagation()}
        data-testid='left-panel'
      >
        <div className='panelHeader'>
          <h3>{t('desktop-left-nav.browse-products')}</h3>
          <button
            onClick={onClose}
            data-testid='left-nav-close-button'
            aria-label={t('desktop-left-nav.close')}
            type='button'
            className={'closeButton'}
          >
            <CloseIcon />
          </button>
        </div>

        <div className='panelContent'>
          <div className='menuLayout'>
            <div data-testid='groupsList' className='groupsList'>
              <div className='groupsItems'>
                {filteredMenuGroups.map((group) => (
                  <button
                    key={group.guid}
                    className={classnames('groupItem', {
                      selected: currentGroup?.guid === group.guid,
                      currentCategory: group.slug === currentCategorySlug
                    })}
                    onClick={() => handleGroupClick(group)}
                  >
                    <span className='groupName'>{group.name}</span>
                    {group.children && group.children.length > 0 && (
                      <ChevronRightIcon
                        size='xs'
                        accessibility='semantic'
                        aria-label={t('desktop-left-nav.has-subcategories')}
                        className='chevronIcon'
                        data-testid='chevron-right'
                      />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {filteredCurrentGroup && (
              <div className='childrenList' data-testid='children-list'>
                <div className='childrenListHeader'>
                  <button
                    className='viewAllButton'
                    onClick={() => {
                      if (filteredCurrentGroup.slug) {
                        navigateToCategory(
                          filteredCurrentGroup,
                          ecommPath,
                          history,
                          onClose
                        )
                      } else {
                        applyFilter(filteredCurrentGroup.guid)
                      }
                    }}
                  >
                    {t('desktop-left-nav.view-all', {
                      name: filteredCurrentGroup.name
                    })}
                  </button>
                </div>
                <div className='childrenItems'>
                  {filteredCurrentGroup?.children?.length ? (
                    <>
                      {filteredCurrentGroup.children.map((child) => (
                        <button
                          key={child.guid}
                          className={classnames('childItem', {
                            selected: selectedChild?.guid === child.guid,
                            currentCategory: child.slug === currentCategorySlug
                          })}
                          onClick={() => handleChildClick(child)}
                        >
                          <span
                            className='childName'
                            data-testid='children-item-name'
                          >
                            {child.name}
                          </span>
                          {child.children &&
                            filterMenuGroupsByItemCount(child.children).length >
                              0 && (
                              <ChevronRightIcon
                                size='xs'
                                accessibility='semantic'
                                aria-label={t(
                                  'desktop-left-nav.has-subcategories'
                                )}
                                className='chevronIcon'
                                data-testid='chevron-right'
                              />
                            )}
                        </button>
                      ))}
                    </>
                  ) : (
                    <p className='noChildren'>
                      {t('desktop-left-nav.no-subcategories-available')}
                    </p>
                  )}
                </div>
              </div>
            )}

            {filteredSelectedChild && (
              <div
                className='grandchildrenList'
                data-testid='grandchildren-list'
              >
                <div className='grandchildrenListHeader'>
                  <button
                    className='viewAllButton'
                    onClick={() => {
                      if (filteredSelectedChild.slug) {
                        navigateToCategory(
                          filteredSelectedChild,
                          ecommPath,
                          history,
                          onClose
                        )
                      } else {
                        const parentGroup = internalSelectedGroup
                        if (parentGroup && filteredSelectedChild) {
                          applyFilter(
                            `${parentGroup.guid}:${filteredSelectedChild.guid}`
                          )
                        }
                      }
                    }}
                  >
                    {t('desktop-left-nav.view-all', {
                      name: filteredSelectedChild.name
                    })}
                  </button>
                </div>
                <div className='grandchildrenItems'>
                  {filteredSelectedChild?.children?.length ? (
                    <>
                      {filteredSelectedChild.children.map((grandchild) => (
                        <button
                          key={grandchild.guid}
                          className={classnames('grandchildItem', {
                            currentCategory:
                              grandchild.slug === currentCategorySlug
                          })}
                          onClick={() => {
                            if (grandchild.slug) {
                              navigateToCategory(
                                grandchild,
                                ecommPath,
                                history,
                                onClose
                              )
                            } else {
                              const parentGroup = internalSelectedGroup
                              if (parentGroup && filteredSelectedChild) {
                                applyFilter(
                                  `${parentGroup.guid}:${filteredSelectedChild.guid}:${grandchild.guid}`
                                )
                              }
                            }
                          }}
                        >
                          <span className='grandchildName'>
                            {grandchild.name}
                          </span>
                        </button>
                      ))}
                    </>
                  ) : (
                    <p className='noGrandchildren'>
                      {t('desktop-left-nav.no-sub-items-available')}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
